// deps
import React, { ReactElement, useCallback } from "react";

// internal-deps
import { InputFieldAttributes, isEmpty, replaceAllOccurence } from "@swiggy-private/core-ui";

// constants
import { TEXTS } from "@payment-ui/constants/index";
import { CARD_BIN_NUMBER_MAX_LIMIT } from "@payment-ui/constants/Card";

// helpers
import { getCardValidator, getTransformValue, isValidCardNumber } from "@payment-ui/helpers/Card";

// test-ids
import { ADD_NEW_CARD_TEST_IDS } from "@swiggy-private/payment-test-ids";

//interface
import { CardBrandDetails, InputCardDetails } from "@swiggy-private/payment-types";

const cardInputs = {
    cardNumber: {
        attrs: {
            autoComplete: "off",
            "data-testid": ADD_NEW_CARD_TEST_IDS.CARD_NUMBER,
            "data-disable-copy-paste": "true",
        },
        errorLabel: TEXTS.INVALID_CARD_NUMBER,
        label: TEXTS.CARD_NUMBER,
        maxLength: 23,
        name: "cardNumber",
        type: "tel",
    },
    cvv: {
        attrs: {
            autoComplete: "off",
            pattern: "[0-9]*",
            inputMode: "numeric",
            "data-testid": ADD_NEW_CARD_TEST_IDS.CVV,
            "data-disable-copy-paste": "true",
        } as InputFieldAttributes,
        errorLabel: TEXTS.INVALID_CVV,
        label: TEXTS.CVV,
        maxLength: 4,
        name: "cvv",
        type: "password",
    },
    expiry: {
        attrs: {
            autoComplete: "off",
            "data-testid": ADD_NEW_CARD_TEST_IDS.EXPIRY,
            "data-disable-copy-paste": "true",
        },
        errorLabel: TEXTS.INVALID_EXPIRY_DATE,
        label: `${TEXTS.VALID_THROUGH.toUpperCase()} (${TEXTS.MONTH_FORMAT}/${TEXTS.YEAR_FORMAT})`,
        labelV4: `${TEXTS.VALID_THROUGH} (${TEXTS.MONTH_FORMAT}/${TEXTS.YEAR_FORMAT})`,
        maxLength: 5,
        name: "expiry",
        type: "tel",
    },
    name: {
        attrs: {
            autoComplete: "cc-name",
            "data-testid": ADD_NEW_CARD_TEST_IDS.CARD_HOLDER_NAME,
        },
        label: TEXTS.NAME_ON_THE_CARD,
        errorLabel: TEXTS.INVALID_CARD_NAME,
        maxLength: 50,
        name: "name",
        type: "text",
    },
    nickName: {
        attrs: {
            "data-testid": ADD_NEW_CARD_TEST_IDS.CARD_HOLDER_NICKNAME,
        },
        label: TEXTS.CARD_NICKNAME,
        errorLabel: TEXTS.INVALID_NICKNAME,
        maxLength: 50,
        name: "nickName",
        type: "text",
    },
};

export interface DefaultDetailsLayoutProps {
    children: (renderProps: DetailsRenderProps) => React.ReactElement;
    details: {
        cardNumber: string;
        cvv: string;
        name: string;
        expiry: string;
        nickName: string;
    };
    errors: {
        cardNumber: boolean;
        cvv: boolean;
        expiry: boolean;
        name: boolean;
        nickName: boolean;
    };
    updateDetails: (data: InputCardDetails) => void;
    resetError: (data: string) => void;
    cardBrandDetails: CardBrandDetails;
    onCardNumberChange: (cardBin: string) => void;
    onCardNumberBlur?: (cardNumber: string) => void;
}

export interface DetailsRenderProps {
    cardBrand: string | null;
    cardIcon: { id: string; padding: number } | null;
    cardInputs: typeof cardInputs;
    details: DefaultDetailsLayoutProps["details"];
    errors: DefaultDetailsLayoutProps["errors"];
    url: string | null;
    onInputChange: (value: string | number, name?: string) => void;
    onCardNumberInputBlur: (cardNumber?: string) => void;
    cardNumberErrorMessage?: string;
}

export default function DetailsContent(props: DefaultDetailsLayoutProps): ReactElement {
    const {
        cardBrandDetails: cardDetails,
        details,
        updateDetails,
        errors,
        resetError,
        onCardNumberChange,
        onCardNumberBlur,
    } = props;

    const onInputChange = useCallback(
        (value: string | number, name?: string) => {
            if (name === undefined) {
                return;
            }

            const _value = getTransformValue({ name, value, details });
            if (_value === undefined) {
                return;
            }

            updateDetails({ ...details, [name]: _value });
            resetError(name);

            /**  Detect card brand using bin */
            if (name === "cardNumber") {
                const cardNumber = String(value).replace(/[\s]/g, "");
                if (cardDetails.cardBrand && cardNumber.length < CARD_BIN_NUMBER_MAX_LIMIT) {
                    onCardNumberChange(cardNumber);
                } else if (!cardDetails.cardBrand && cardNumber.length >= CARD_BIN_NUMBER_MAX_LIMIT) {
                    onCardNumberChange(cardNumber.substring(0, CARD_BIN_NUMBER_MAX_LIMIT));
                }
            }
        },
        [cardDetails.cardBrand, details, onCardNumberChange, resetError, updateDetails],
    );

    const onCardNumberInputBlur = useCallback(() => {
        const validator = getCardValidator(details.cardNumber.trim());
        if (!isEmpty(onCardNumberBlur) && isValidCardNumber(validator)) {
            onCardNumberBlur(replaceAllOccurence(details.cardNumber, " ", ""));
        }
    }, [details, onCardNumberBlur]);

    return props.children({
        ...cardDetails,
        cardInputs,
        details,
        errors,
        onInputChange,
        onCardNumberInputBlur,
    });
}
